/**
 * Station Routing Schemas
 *
 * Zod schemas for multi-station routing - allows creating named station
 * instances with routing rules that determine which orders each station handles.
 *
 * Primary use case: Packing station routing (e.g., PAP single-line → Packing 1,
 * everything else → Packing 2).
 */
import { z } from 'zod';
// ============================================================================
// ORDER CLASSIFICATION
// ============================================================================
export const ProductionMethodSchema = z.enum(['POD', 'PAP', 'MIXED']);
export const LineCountSchema = z.enum(['SINGLE', 'MULTI']);
export const OrderClassificationSchema = z.object({
    productionMethod: ProductionMethodSchema,
    lineCount: LineCountSchema,
});
// ============================================================================
// ROUTING RULES (stored in prod_stations.settings JSONB)
// ============================================================================
export const PackingRoutingRulesSchema = z.object({
    productionMethod: ProductionMethodSchema.nullable().optional(),
    lineCount: LineCountSchema.nullable().optional(),
    isCatchAll: z.boolean().optional(),
});
// ============================================================================
// STATION CRUD SCHEMAS
// ============================================================================
export const CreatePackingStationSchema = z.object({
    locationId: z.string().uuid(),
    name: z.string().min(1).max(50),
    routing: PackingRoutingRulesSchema.optional(),
    /** BLU-1460: this packing station is the floor's hand over bench. */
    handoverBench: z.boolean().optional(),
});
export const UpdatePackingStationSchema = z.object({
    stationId: z.string().uuid(),
    name: z.string().min(1).max(50).optional(),
    routing: PackingRoutingRulesSchema.optional(),
    isActive: z.boolean().optional(),
    skipVerification: z.boolean().optional(),
    /** BLU-1460: flag or unflag the station as the hand over bench. */
    handoverBench: z.boolean().optional(),
});
export const DeletePackingStationSchema = z.object({
    stationId: z.string().uuid(),
});
// ============================================================================
// ASSIGNMENT SCHEMAS
// ============================================================================
export const AssignUserToStationSchema = z.object({
    stationId: z.string().uuid(),
    profileId: z.string().uuid(),
});
export const UnassignUserSchema = z.object({
    profileId: z.string().uuid(),
});
// ============================================================================
// QUERY SCHEMAS
// ============================================================================
export const GetPackingStationsSchema = z.object({
    locationId: z.string().uuid(),
});
export const GetMyStationSchema = z.object({
    locationId: z.string().uuid(),
});
